import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setAssistantStatus, setWaiting, markEventsStale } from '../store/chatSlice';
import { sendAction } from '../store/Actions';
import TypingEffect from '../TypingEffect';
import MarkdownText from '../MarkdownText';
import { CardFor } from '../cards';
import { cardFreshnessKey } from '../cards/freshnessKey';

// A structured event is anything that carries one of: a markdown-style
// `text`, a `kind` (registry-routed card), or `action`/`actions` (an
// inline action button). Anything else is plain progress text. Cards
// whose layout doesn't reduce to a single `text` field (e.g.
// tobin.restaurant_info with name/cuisine/address/specialties) need
// `kind` to be recognized as structured here, otherwise the value
// falls through to the plain-text branch and renders as raw JSON.
function parseEvent(msg) {
  const isStructured = (v) => v && typeof v === 'object' &&
    (v.text || v.kind || v.action || v.actions);
  if (isStructured(msg)) return msg;
  try {
    const parsed = JSON.parse(msg);
    if (isStructured(parsed)) return parsed;
  } catch (e) { /* plain text */ }
  return null;
}

const TextViewer = ({ events }) => {
  const dispatch = useDispatch();
  const { station, threadId, user } = useSelector(state => state.chat);

  const handleAction = (action, structured) => {
    // embeddable-card-live-action: a read-only embed has no station/WS (it's a
    // one-shot fetch), so card actions are inert — bail before touching
    // station.station.station_id. (ADR: interactive embeds are a separate tier.)
    if (!station || !station.station) return;
    dispatch(setWaiting(true));
    // Soft-clear: prior turn's events stay visible until the FIRST event
    // of THIS click's response lands. Same pattern as submit-time so the
    // panel doesn't go blank between click and the action's first card.
    dispatch(markEventsStale());
    dispatch(sendAction(action, station.station.station_id, station.sessionId, threadId));
    const confirmation = action.confirmation
      || (structured.summary
        ? `${action.label || 'Action'}: ${structured.summary}`
        : (action.label || 'Action'));
    // ephemeral-status-beats: show the action's confirmation as a TRANSIENT status
    // line (like "checking…"), not a persisted system bubble — cleared when the
    // action's reply lands (websocketMiddleware clears it on any real reply).
    dispatch(setAssistantStatus(confirmation));
  };

  // Plain-text progress events fall into two buckets:
  //   • "sticky"  — opening status like "Searching across 60 sources…",
  //                 stays visible the whole search phase.
  //   • "transient" — per-result labels like `✓ Restaurant`; only the
  //                   most recent one shows, so they overwrite in place.
  // Once any structured event lands (top-N ranked cards or the wrapped
  // summary), the plain-text progress drops out entirely — the cards
  // carry the information in a richer form.
  const decoratedEvents = (events || [])
    .map((msg, originalIndex) => ({ msg, originalIndex, structured: parseEvent(msg) }));
  const structured = decoratedEvents.filter(e => e.structured);
  const isTransient = (e) => typeof e.msg === 'string' && e.msg.trim().startsWith('✓');

  // Deduplicate sticky plain-text entries by trimmed content — prevents
  // re-emitting the same status line (e.g. "Searching across 60 sources…")
  // from stacking when the user fires multiple queries in a row.
  const stickyPlain = [];
  const stickySeen = new Set();
  for (const e of decoratedEvents) {
    if (e.structured || isTransient(e)) continue;
    const key = typeof e.msg === 'string' ? e.msg.trim() : JSON.stringify(e.msg);
    if (stickySeen.has(key)) continue;
    stickySeen.add(key);
    stickyPlain.push(e);
  }

  const lastTransient = [...decoratedEvents].reverse().find(e => !e.structured && isTransient(e));
  const orderedEvents = structured.length > 0
    ? structured
    : [...stickyPlain, ...(lastTransient ? [lastTransient] : [])];

  // output-idle-logo: with nothing to render, this used to still paint its own
  // chrome — a white, bordered, rounded, flex:1 box — so an untouched Output
  // panel showed an empty card. Render nothing instead, so the panel is actually
  // empty and the idle watermark behind it is what you see.
  //
  // `orderedEvents` is empty exactly when `events` is (a lone transient still
  // yields lastTransient), so this matches ChatInterface's showIdleWatermark
  // gate — the box disappears precisely when the watermark appears.
  if (orderedEvents.length === 0) return null;

  return (
    <div style={{ flex: 1, overflowY: 'scroll', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '10px', background: '#ffffff' }}>
      {orderedEvents.map(({ msg, originalIndex, structured }) => {
        const index = originalIndex;
        if (structured && (structured.kind || structured.action || structured.actions)) {
          // Card — dispatch to a domain-specific component through the
          // cards/ registry. TextViewer no longer inspects domain fields
          // (image_url, restaurant, score, …) directly; layout lives in
          // the registered component for the value's `kind` (GenericCard
          // is the fallback for anything unregistered).
          // A sandboxed card gets its data via host:init ONCE at iframe load, so the
          // key decides whether a new payload is ever seen. This used to fall back to
          // `index` for chat cards — a constant — which froze every card emitted into
          // a conversation on its first payload, until a full page reload.
          // `cards/freshnessKey.js` now owns the rule for BOTH viewers; the two
          // disagreeing, each fixing a different subset, is what caused that.
          const cardKey = cardFreshnessKey(structured, index);
          return (
            <div key={cardKey}>
              <CardFor structured={structured} onAction={handleAction} accountId={user && user.user_id} />
            </div>
          );
        }
        if (structured) {
          // Structured-without-action: prose summary. Render as plain
          // text so it reads as a paragraph, not another card.
          return (
            <div key={index} style={{ margin: '12px 4px', fontSize: '0.9rem', lineHeight: 1.5 }}>
              <MarkdownText text={structured.text} />
            </div>
          );
        }
        return (
          <div key={index} style={{ marginBottom: '10px' }}>
            <TypingEffect
              text={msg}
              delay={5}
              key={`typing-${index}`}
            />
          </div>
        );
      })}
    </div>
  );
};

export default TextViewer;
